import lottoService from '@/service/lottoService'
import {
  ReferenceLottery, ReferenceSourceStatus, ReferenceAction, ReferenceOutcome,
} from '@/service/resultReferenceTypes'

export const supportedLottery = id => Object.values(ReferenceLottery).includes(String(id))

export async function referenceRequest(method, path, body) {
  const response = await lottoService.send(`${lottoService.getURL()}result-reference/${path}`, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }, { preserveSession: true })
  if (response.status !== 200) {
    throw new Error(typeof response.data === 'string' ? response.data : 'ไม่สามารถติดต่อระบบตรวจผลอ้างอิงได้ กรุณาลองอีกครั้ง')
  }
  return response.data
}

// fullNumberLength: digits of the round's full number (0 = the round has none). A source may publish only its
// trailing digits, so the full-number row is compared as a suffix (`suffix: true`).
export const referenceRows = (id, fullNumberLength = 0) => (String(id) === ReferenceLottery.ThaiGovernment ? [
  { label: 'รางวัลที่ 1', keys: ['topThree'] },
  { label: 'สามตัวหน้า', keys: ['topThreeFront1', 'topThreeFront2'] },
  { label: 'สามตัวหลัง', keys: ['topThreeBack1', 'topThreeBack2'] },
  { label: 'สองตัวล่าง', keys: ['bottomTwo'] },
] : [
  { label: 'สามตัวบน', keys: ['topThree'] },
  { label: 'สองตัวล่าง', keys: ['bottomTwo'] },
  ...(fullNumberLength > 0 ? [{
    label: 'เลขเต็ม', keys: ['fullNumber'], length: fullNumberLength, suffix: true,
  }] : []),
])

// Equal values: for the full-number row, only a source with every digit can equal the input.
export const rowMatches = (a, b, row) => row.keys.map(key => (a && a[key]) || '').sort().join(',')
  === row.keys.map(key => (b && b[key]) || '').sort().join(',')

const fullValue = (numbers, row) => String((numbers && numbers[row.keys[0]]) || '')

// Digits the source publishes for the full-number row (0 = none); other rows are always fully known.
export const knownDigits = (source, row) => (row.suffix ? fullValue(source, row).length : Infinity)

// The input does not contradict the source: equal, or for the full-number row ending with the source's digits.
export const rowAgrees = (input, source, row) => {
  if (!row.suffix) return rowMatches(input, source, row)
  const known = fullValue(source, row)
  return !known || fullValue(input, row).endsWith(known)
}

// Prize pairs are unordered, so sort them for display; otherwise equal results look different.
// masked: a source's full number shows its unknown leading digits as "•".
export const pairValue = (numbers, row, masked = false) => {
  const values = row.keys.map(key => (numbers && numbers[key]) || '')
  if (values.every(value => !value)) return '—'
  if (row.suffix && masked) return values[0].padStart(row.length, '•')
  return values.sort().map(value => value || '—').join(' / ')
}

export const dateLocale = locale => (locale === 'th' ? 'th-TH' : 'en-GB')

// drawDate is a calendar date (yyyy-MM-dd); build it locally so no timezone shifts the day.
export const drawDateLabel = (value, locale) => {
  const [year, month, day] = String(value || '').split('-').map(Number)
  if (!year || !month || !day) return '—'
  return new Date(year, month - 1, day).toLocaleDateString(dateLocale(locale), { day: 'numeric', month: 'long', year: 'numeric' })
}

export const statusLabel = status => ({
  [ReferenceSourceStatus.Available]: 'มีผลอ้างอิง',
  [ReferenceSourceStatus.Pending]: 'ยังไม่มีผลครบ',
  [ReferenceSourceStatus.WrongDraw]: 'ข้อมูลไม่ตรงงวด',
  [ReferenceSourceStatus.Unavailable]: 'ดึงข้อมูลไม่ได้',
}[status] || 'ดึงข้อมูลไม่ได้')

const escapeHtml = value => String(value == null ? '' : value).replace(/[&<>"']/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[char]))

// Feather icon paths, inlined because SweetAlert takes an HTML string rather than components.
const iconPaths = {
  check: '<polyline points="20 6 9 17 4 12"></polyline>',
  alert: '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line>',
  x: '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>',
}
const icon = (name, size = 12) => `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${iconPaths[name]}</svg>`

export async function confirmReference(vm, check) {
  const t = (text, values) => escapeHtml(vm.$t(text, values))
  const rows = referenceRows(check.huayId, String((check.numbers && check.numbers.fullNumber) || '').length)
  const available = check.sources.filter(source => source.status === ReferenceSourceStatus.Available)
  const differs = (source, row) => !rowAgrees(check.numbers, source.numbers, row)
  // Trailing digits of the full number that some source confirmed; fewer than the row's length leaves the rest to the operator.
  const confirmedDigits = row => Math.min(row.length, Math.max(0, ...available.map(source => knownDigits(source.numbers, row))))
  const isShort = row => row.suffix && available.length > 0 && confirmedDigits(row) < row.length
  const title = check.requiresReason ? 'เลขที่กรอกไม่ตรงกับแหล่งอ้างอิง' : 'ตรวจสอบเลขรางวัลก่อนยืนยัน'
  const explanations = {
    [ReferenceOutcome.Mismatch]: 'พบเลขแตกต่างจากแหล่งอ้างอิง กรุณาตรวจงวดและเลขอีกครั้ง หากยืนยันว่าเลขของคุณถูกต้อง ให้ระบุเหตุผลก่อนดำเนินการต่อ',
    [ReferenceOutcome.Unavailable]: 'ยังตรวจสอบกับแหล่งอ้างอิงไม่ได้ กรุณาตรวจเลขที่แสดงด้วยตนเองก่อนยืนยัน',
    [ReferenceOutcome.Partial]: rows.some(isShort) && available.length === check.sources.length
      ? 'แหล่งอ้างอิงยืนยันเลขเต็มได้ไม่ครบทุกหลัก กรุณาตรวจหลักที่เหลือด้วยตนเองก่อนยืนยัน'
      : 'ตรวจสอบได้เพียงบางแหล่ง กรุณาตรวจเลขและข้อมูลที่ยังขาดก่อนยืนยัน',
    [ReferenceOutcome.Match]: 'เลขตรงกับแหล่งอ้างอิงที่เลือก กรุณาตรวจงวดและเลขก่อนยืนยัน',
  }
  // Sources are compared with the numbers being confirmed, never with each other by majority.
  const chips = check.sources.map(source => {
    if (source.status !== ReferenceSourceStatus.Available) {
      return `<li><span class="reference-chip is-unavailable">${icon('alert')} ${escapeHtml(source.name)} <span class="reference-chip-meta">· ${t(statusLabel(source.status))}</span></span></li>`
    }
    const count = rows.filter(row => differs(source, row)).length
    return count
      ? `<li><span class="reference-chip is-mismatch">${icon('x')} ${escapeHtml(source.name)} <span class="reference-chip-meta">· ${t('ไม่ตรง {count} รางวัล', { count })}</span></span></li>`
      : `<li><span class="reference-chip is-agree">${icon('check')} ${escapeHtml(source.name)}</span></li>`
  }).join('')
  const results = rows.map(row => {
    const groups = []
    available.filter(source => differs(source, row)).forEach(source => {
      const value = pairValue(source.numbers, row, true)
      const group = groups.find(item => item.value === value)
      if (group) group.names.push(source.name)
      else groups.push({ value, names: [source.name] })
    })
    let detail = ''
    if (groups.length) {
      detail = groups.map(group => `<span class="reference-result-input is-mismatch">${escapeHtml(group.names.join(', '))}: ${escapeHtml(group.value)}</span>`).join('')
    } else if (isShort(row)) {
      const count = confirmedDigits(row)
      detail = `<span class="reference-result-input is-suffix">${icon('alert')} ${count ? t('ตรง {count} หลักท้าย', { count }) : t('แหล่งอ้างอิงไม่มีเลขเต็ม')}</span>`
    } else if (available.length) {
      detail = `<span class="reference-result-input is-match">${icon('check')} ${t('ตรงกับแหล่งอ้างอิง')}</span>`
    }
    return `<div class="reference-result${groups.length ? ' is-conflict' : ''}"><dt>${groups.length ? `${icon('alert', 13)} ` : ''}${t(row.label)}</dt><dd><span class="reference-result-value">${escapeHtml(pairValue(check.numbers, row))}</span>${detail}</dd></div>`
  }).join('')
  const result = await vm.$swal({
    title: vm.$t(title),
    icon: check.outcome === ReferenceOutcome.Match ? 'info' : 'warning',
    width: 560,
    html: `<div class="reference-confirm">
      <div><div class="reference-details-lottery">${escapeHtml(check.huayName)}</div><div class="reference-details-date">${t('งวดวันที่')} ${escapeHtml(drawDateLabel(check.drawDate, vm.$i18n.locale))}</div></div>
      <p class="reference-confirm-lead">${t(explanations[check.outcome])}</p>
      ${chips ? `<ul class="reference-chips" aria-label="${t('แหล่งอ้างอิงผลหวย')}">${chips}</ul>` : ''}
      <dl class="reference-results">${results}</dl>
    </div>`,
    ...(check.requiresReason ? {
      input: 'textarea',
      inputLabel: vm.$t('เหตุผลที่ยืนยันใช้เลขนี้'),
      inputPlaceholder: vm.$t('ระบุข้อมูลที่ใช้ตรวจและเหตุผลที่ยืนยันเลข'),
      inputAttributes: { maxlength: '1000' },
      inputValidator: value => (!value || !value.trim() ? vm.$t('กรุณาระบุเหตุผลก่อนยืนยัน') : undefined),
    } : {}),
    showCancelButton: true,
    confirmButtonText: vm.$t(check.action === ReferenceAction.Dispatch ? 'ยืนยันจ่ายเครดิตตามเลขนี้' : 'ยืนยันคำนวณตามเลขนี้'),
    cancelButtonText: vm.$t('กลับไปตรวจเลข'),
    allowOutsideClick: false,
    customClass: { popup: 'reference-confirm-popup', confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary reference-outline-button ml-1' },
    buttonsStyling: false,
  })
  if (!result.isConfirmed) return null
  return { checkId: check.id, confirmed: true, reason: check.requiresReason ? result.value.trim() : null }
}
